/** Email auth helpers (OTP + remembered browser). */

export function isPhoneAuthEnabled() {
  const raw = (process.env.REACT_APP_PHONE_AUTH_ENABLED || 'false').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

export function sessionRecallHours() {
  const n = Number(process.env.REACT_APP_SESSION_RECALL_HOURS || 48);
  return Number.isFinite(n) && n > 0 ? n : 48;
}

export function maskEmail(email) {
  const value = String(email || '').trim();
  if (!value.includes('@')) return value;
  const [local, domain] = value.split('@');
  if (local.length <= 2) return `${local.slice(0, 1)}*@${domain}`;
  const stars = '*'.repeat(Math.min(local.length - 2, 4));
  return `${local[0]}${stars}${local[local.length - 1]}@${domain}`;
}

/** RFC-ish local@domain; rejects control chars and header/HTML injection vectors. */
const EMAIL_MAX_LENGTH = 254;
const EMAIL_PATTERN =
  /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

/**
 * Normalize and validate an email for login/OTP.
 * @returns {{ ok: true, email: string } | { ok: false, error: string }}
 */
export function normalizeAndValidateEmail(raw) {
  if (raw == null) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  const email = String(raw).trim().toLowerCase();

  if (!email) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  if (email.length > EMAIL_MAX_LENGTH) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  // Block control chars, CRLF (header injection), and HTML/script delimiters.
  for (let i = 0; i < email.length; i += 1) {
    const code = email.charCodeAt(i);
    if (code < 32 || code === 127) {
      return { ok: false, error: 'Enter a valid email address.' };
    }
  }
  if (/[<>"\\]/.test(email)) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  if ((email.match(/@/g) || []).length !== 1) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  if (!EMAIL_PATTERN.test(email)) {
    return { ok: false, error: 'Enter a valid email address.' };
  }

  return { ok: true, email };
}

/** Digits-only OTP; rejects anything else. */
export function normalizeOtpCode(raw) {
  return String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);
}

const RECALL_KEY = 'webchat_recall_profile';

export function saveRecallProfile({ email, lastSeenAt, channelName, channelId }) {
  const validated = normalizeAndValidateEmail(email);
  if (!validated.ok) return;

  try {
    localStorage.setItem(
      RECALL_KEY,
      JSON.stringify({
        maskedEmail: maskEmail(validated.email),
        email: validated.email,
        lastSeenAt: lastSeenAt || null,
        channelName: String(channelName || '').slice(0, 120),
        channelId: String(channelId || '').slice(0, 64),
        savedAt: new Date().toISOString(),
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadRecallProfile() {
  try {
    const raw = localStorage.getItem(RECALL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const validated = normalizeAndValidateEmail(parsed.email);
    if (!validated.ok) {
      clearRecallProfile();
      return null;
    }
    return {
      ...parsed,
      email: validated.email,
      maskedEmail: maskEmail(validated.email),
    };
  } catch {
    return null;
  }
}

export function clearRecallProfile() {
  try {
    localStorage.removeItem(RECALL_KEY);
  } catch {
    /* ignore */
  }
}
